import { getAboutFieldFormatError, isValidAbsoluteUrl } from "./reportValidation";

/**
 * Fields the API validates for format (email / phone / absolute URL) that this app already
 * validates identically via reportValidation.js. Maps the evaluation-state path (used by the
 * About page's inline errors) to where that value lands in the create/update report payload.
 */
const SANITIZED_PAYLOAD_FIELDS = [
  { evaluationPath: "author.email", containerKey: "author", payloadKey: "email" },
  { evaluationPath: "author.phone", containerKey: "author", payloadKey: "phone" },
  { evaluationPath: "author.website", containerKey: "author", payloadKey: "websiteUrl" },
  { evaluationPath: "vendor.email", containerKey: "vendorContact", payloadKey: "email" },
  { evaluationPath: "vendor.phone", containerKey: "vendorContact", payloadKey: "phone" },
  { evaluationPath: "vendor.website", containerKey: "vendorContact", payloadKey: "websiteUrl" },
  { evaluationPath: "repository", containerKey: "reportDetails", payloadKey: "repositoryUrl" },
  { evaluationPath: "feedback", containerKey: "reportDetails", payloadKey: "feedbackUrl" },
];

function getEvaluationValue(evaluation, evaluationPath) {
  return evaluationPath
    .split(".")
    .reduce((value, key) => (value == null ? undefined : value[key]), evaluation);
}

/**
 * Returns a copy of an outgoing report create/update payload with any field blanked whose current
 * value would fail the API's format validation (email / phone / absolute URL), so a malformed
 * value never reaches the server and never blocks autosave. The evaluation state itself -- and any
 * inline "About" page error already shown to the user -- is left completely untouched; only the
 * returned payload differs from the input. Does not mutate `payload`.
 */
export default function sanitizeReportPayloadForSave(payload, evaluation) {
  if (!payload) return payload;

  const containerUpdates = {};
  SANITIZED_PAYLOAD_FIELDS.forEach(({ evaluationPath, containerKey, payloadKey }) => {
    if (!payload[containerKey]) return;
    const value = getEvaluationValue(evaluation, evaluationPath);
    if (!getAboutFieldFormatError(evaluationPath, value)) return;
    containerUpdates[containerKey] = {
      ...(containerUpdates[containerKey] ?? payload[containerKey]),
      [payloadKey]: "",
    };
  });

  const originalRelatedAcrs = payload.reportDetails?.relatedAcrs;
  if (Array.isArray(originalRelatedAcrs)) {
    const filteredRelatedAcrs = originalRelatedAcrs.filter(isValidAbsoluteUrl);
    if (filteredRelatedAcrs.length !== originalRelatedAcrs.length) {
      containerUpdates.reportDetails = {
        ...(containerUpdates.reportDetails ?? payload.reportDetails),
        relatedAcrs: filteredRelatedAcrs,
      };
    }
  }

  if (Object.keys(containerUpdates).length === 0) return payload;
  return { ...payload, ...containerUpdates };
}
